// Whiteboard pure operations (Phase 3.6B.3): layering, grouping, flags. Additive optional fields only:
// `groupId` (string), `hidden` (bool), `locked` (bool). Old boards without them behave exactly as before.
import { uid, moveObj } from './whiteboardShapes';
import { drawnBox } from './wbGeometry';

const TYPE_LABEL = { rect: 'Rectangle', ellipse: 'Ellipse', line: 'Line', arrow: 'Arrow', path: 'Shape', text: 'Text', image: 'Image' };

export function layerName(o) {
  if (o.type === 'text') {
    const t = String(o.text || '').split('\n')[0].trim();
    return t ? (t.length > 28 ? `${t.slice(0, 27)}…` : t) : 'Text';
  }
  if (o.type === 'path' && !o.closed) return 'Drawing';
  if (o.type === 'image') return o.name === 'AI illustration' ? 'Picture · AI illustration' : 'Picture · Image';
  return TYPE_LABEL[o.type] || 'Object';
}

export const groupMembers = (objs, gid) => objs.filter((o) => o.groupId === gid);

export function groupName(objs, gid) {
  const m = groupMembers(objs, gid);
  const t = m.find((o) => o.type === 'text' && String(o.text || '').trim());
  return t ? `Group · ${layerName(t)}` : `Group of ${m.length}`;
}

export const sameSet = (a, b) => a.length === b.length && a.every((x) => b.includes(x));

export function moveSelection(objs, ids, dir) {
  const sel = new Set(ids);
  if (dir === 'front') return [...objs.filter((o) => !sel.has(o.id)), ...objs.filter((o) => sel.has(o.id))];
  if (dir === 'back') return [...objs.filter((o) => sel.has(o.id)), ...objs.filter((o) => !sel.has(o.id))];
  const n = [...objs];
  if (dir === 'forward') {
    for (let i = n.length - 2; i >= 0; i--) if (sel.has(n[i].id) && !sel.has(n[i + 1].id)) [n[i], n[i + 1]] = [n[i + 1], n[i]];
  } else {
    for (let i = 1; i < n.length; i++) if (sel.has(n[i].id) && !sel.has(n[i - 1].id)) [n[i], n[i - 1]] = [n[i - 1], n[i]];
  }
  return n;
}

const stripGroup = ({ groupId, ...rest }) => rest;

// Group = shared groupId; members become contiguous at the topmost member's layer position.
export function groupSelection(objs, ids) {
  if (ids.length < 2) return objs;
  const gid = `g${uid()}`;
  const sel = new Set(ids);
  const top = Math.max(...objs.map((o, i) => (sel.has(o.id) ? i : -1)));
  const before = objs.slice(0, top + 1).filter((o) => !sel.has(o.id));
  const after = objs.slice(top + 1);
  return [...before, ...objs.filter((o) => sel.has(o.id)).map((o) => ({ ...o, groupId: gid })), ...after];
}

export function ungroupSelection(objs, ids) {
  const gids = new Set(objs.filter((o) => ids.includes(o.id) && o.groupId).map((o) => o.groupId));
  return objs.map((o) => (gids.has(o.groupId) ? stripGroup(o) : o));
}

export const patchMany = (objs, ids, patch) => objs.map((o) => (ids.includes(o.id) ? { ...o, ...patch } : o));

export function duplicateSelection(objs, ids) {
  const gmap = {};
  let copies = objs.filter((o) => ids.includes(o.id)).map((o) => {
    const c = { ...moveObj(o, 20, 20), id: uid() };
    if (o.groupId) { gmap[o.groupId] = gmap[o.groupId] || `g${uid()}`; c.groupId = gmap[o.groupId]; }
    return c;
  });
  copies = copies.map((c) => (c.groupId && copies.filter((x) => x.groupId === c.groupId).length < 2 ? stripGroup(c) : c));
  return { objects: [...objs, ...copies], ids: copies.map((c) => c.id) };
}

export function unionBox(list) {
  if (!list.length) return null;
  const bs = list.map(drawnBox);
  const x = Math.min(...bs.map((b) => b.x)), y = Math.min(...bs.map((b) => b.y));
  return { x, y, w: Math.max(...bs.map((b) => b.x + b.w)) - x, h: Math.max(...bs.map((b) => b.y + b.h)) - y };
}

// Canvas press: keep the current selection if the object is in it (drag them all); otherwise select its unit
// (the whole group for grouped objects). Double-click / the Layers panel select a single member.
export function clickSelection(objs, current, o) {
  if (current.includes(o.id)) return current;
  return o.groupId ? groupMembers(objs, o.groupId).map((m) => m.id) : [o.id];
}

export function toggleUnit(objs, current, o) {
  const unit = o.groupId ? groupMembers(objs, o.groupId).map((m) => m.id) : [o.id];
  const allIn = unit.every((id) => current.includes(id));
  return allIn ? current.filter((id) => !unit.includes(id)) : [...new Set([...current, ...unit])];
}

// Layers rows, front-to-back; group members are listed together under their group row.
export function layerRows(objs) {
  const rows = [];
  const seen = new Set();
  for (let i = objs.length - 1; i >= 0; i--) {
    const o = objs[i];
    if (!o.groupId) { rows.push({ kind: 'obj', o, depth: 0 }); continue; }
    if (seen.has(o.groupId)) continue;
    seen.add(o.groupId);
    const members = groupMembers(objs, o.groupId).reverse();
    rows.push({ kind: 'group', gid: o.groupId, members });
    members.forEach((m) => rows.push({ kind: 'obj', o: m, depth: 1 }));
  }
  return rows;
}
